import { useMemo, useState, type FormEvent } from "react";
import {
  Badge,
  Banner,
  Button,
  Card,
  ConfirmationModal,
  DataList,
  FieldTextarea,
  LoadingAnimation,
  Modal,
  Stack,
  Table,
  type TableColumn,
  useToast,
} from "@knowtex-ai/components";
import { EditPencil, Plus, Trash } from "iconoir-react";
import { MC_AGENT, type McMemoryEntry } from "../../lib/mc";
import {
  useCreateMcMemory,
  useDeleteMcMemory,
  useMcMemoryQuery,
  useUpdateMcMemory,
} from "../../hooks/useMcRuns";
import ClampedText from "./ClampedText";
import McHeader from "./McHeader";
import McPageShell from "./McPageShell";
import { GAP } from "./layout";
import NewTabLink from "./NewTabLink";
import RowActions from "./RowActions";
import type { McMemoryChange } from "./runData";
import TimeCell from "./TimeCell";
import { useEditorModal } from "./useEditorModal";
interface McMemoryPageProps {
  setAuth: (isAuthenticated: boolean) => void;
  setUserOrg: (org: string | null) => void;
}

const MAX_MEMORY_CHARS = 2000;

const runLink = (runId: string) => (
  <NewTabLink to={`/mc/runs/${runId}`} aria-label={`Open MC run ${runId}`}>
    <span className="font-mono">Run {runId.slice(0, 8)}</span>
  </NewTabLink>
);

const byLine = (who?: string) =>
  who && who !== MC_AGENT ? (
    <div className="truncate text-[13px] text-gray-500" title={who}>
      by {who}
    </div>
  ) : null;

/** Where an entry came from and when it last changed. */
function EntryDetails({ entry }: { entry: McMemoryEntry }) {
  return (
    <DataList
      size="small"
      aria-label="Entry details"
      items={[
        {
          id: "source",
          label: "Added by",
          value: entry.sourceRunId
            ? runLink(entry.sourceRunId)
            : entry.createdBy || "—",
        },
        {
          id: "created",
          label: "Added",
          value: <TimeCell iso={entry.createdAt} />,
        },
        {
          id: "updated",
          label: "Updated",
          value: (
            <div className="min-w-0">
              <TimeCell iso={entry.updatedAt} />
              {byLine(entry.updatedBy)}
            </div>
          ),
        },
      ]}
    />
  );
}

function EntryModal({
  entry,
  open,
  onClose,
}: {
  /** Undefined adds a new entry. */
  entry?: McMemoryEntry;
  open: boolean;
  onClose: () => void;
}) {
  const update = useUpdateMcMemory();
  const create = useCreateMcMemory();
  const [text, setText] = useState(entry?.text ?? "");
  const pending = update.isPending || create.isPending;
  const error = update.error ?? create.error;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const done = { onSuccess: onClose };
    if (entry) update.mutate({ id: entry.id, text: text.trim() }, done);
    else create.mutate({ text: text.trim() }, done);
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={entry ? "Edit memory entry" : "Add to memory"}
      size="medium"
    >
      <form onSubmit={submit} className="flex flex-col gap-5">
        <FieldTextarea
          label="Entry"
          placeholder="e.g. After Stop, wait 2-4 minutes for the note to reach Review. It's generated in the background, so an empty Review tab before then isn't a bug."
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, MAX_MEMORY_CHARS))}
          maxLength={MAX_MEMORY_CHARS}
          autoComplete="off"
          rows={6}
          resize="vertical"
          required
          autoFocus
          disabled={pending}
        />
        {entry && <EntryDetails entry={entry} />}
        {error && (
          <Banner variant="error" heading="Couldn't save the entry">
            {error.message}
          </Banner>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={pending}
            disabled={text.trim().length < 3}
          >
            {entry ? "Save" : "Add"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function McMemory() {
  const toast = useToast();
  const memoryQuery = useMcMemoryQuery();
  const remove = useDeleteMcMemory();
  const editor = useEditorModal<McMemoryEntry>();
  const editEntry = editor.edit;
  const [deleting, setDeleting] = useState<McMemoryEntry | null>(null);
  const entries = useMemo(() => memoryQuery.data ?? [], [memoryQuery.data]);

  const deleteEntry = () => {
    if (!deleting) return;
    remove.mutate(deleting.id, {
      onSuccess: () => toast.success("Removed from memory"),
      onError: (error) =>
        toast.error("Couldn't remove the entry", error.message),
    });
    setDeleting(null);
  };

  const columns = useMemo(
    (): TableColumn<McMemoryEntry>[] => [
      {
        id: "text",
        header: "Entry",
        width: 460,
        grow: true,
        accessor: "text",
        cell: (e) => <ClampedText text={e.text} lines={4} />,
      },
      {
        id: "source",
        header: "From",
        width: 170,
        accessor: (e) => e.sourceRunId ?? e.createdBy ?? "",
        cell: (e) =>
          e.sourceRunId ? (
            runLink(e.sourceRunId)
          ) : (
            <span className="truncate" title={e.createdBy}>
              {e.createdBy || "—"}
            </span>
          ),
      },
      {
        id: "updated",
        header: "Updated",
        width: 220,
        accessor: (e) => new Date(e.updatedAt),
        sortable: true,
        cell: (e) => (
          <div className="min-w-0">
            <TimeCell iso={e.updatedAt} />
            {byLine(e.updatedBy)}
          </div>
        ),
      },
      {
        id: "actions",
        header: <span className="sr-only">Actions</span>,
        width: 64,
        sticky: "end",
        hideable: false,
        cell: (e) => (
          <RowActions
            label={`Actions for memory entry ${e.text.slice(0, 40)}`}
            actions={[
              {
                key: "edit",
                label: "Edit",
                icon: EditPencil,
                onClick: () => editEntry(e),
              },
              {
                key: "delete",
                label: "Delete",
                icon: Trash,
                danger: true,
                onClick: () => setDeleting(e),
              },
            ]}
          />
        ),
      },
    ],
    [editEntry],
  );

  const body = () => {
    if (memoryQuery.isLoading) {
      return (
        <div className="flex justify-center py-24 text-gray-400">
          <LoadingAnimation size={28} />
        </div>
      );
    }
    if (memoryQuery.isError) {
      return (
        <Banner variant="error" heading="Couldn't load memory">
          {memoryQuery.error.message}
        </Banner>
      );
    }
    return (
      <Stack gap={GAP.page}>
        <Banner variant="brand" heading="How memory works">
          Memory is what MC has learned about the app, like how to set up test
          data, which steps are slow and which screens look broken but aren't.
          Every run reads it, and runs add, update and remove entries as they
          go. Their changes apply when the run finishes, so edit or delete
          anything that's wrong.
        </Banner>
        <Table
          aria-label="Memory entries"
          columns={columns}
          data={entries}
          getRowId={(e) => e.id}
          defaultPageSize={50}
          emptyState="Nothing yet. Runs add what they learn, or add your own."
        />
      </Stack>
    );
  };

  return (
    <>
      <McHeader
        section="memory"
        actions={
          <Button icon={Plus} onClick={editor.add}>
            Add entry
          </Button>
        }
      />
      <div className="mt-8">{body()}</div>
      {editor.state && (
        <EntryModal
          key={editor.state.key}
          entry={editor.state.item ?? undefined}
          open={editor.state.open}
          onClose={editor.close}
        />
      )}
      <ConfirmationModal
        open={!!deleting}
        title="Delete this memory entry?"
        continueText="Delete"
        cancelText="Close"
        continueVariant="primary-danger"
        onContinue={deleteEntry}
        onCancel={() => setDeleting(null)}
      >
        Future runs will no longer have access to this memory entry.
      </ConfirmationModal>
    </>
  );
}

export default function McMemoryPage({
  setAuth,
  setUserOrg,
}: McMemoryPageProps) {
  return (
    <McPageShell setAuth={setAuth} setUserOrg={setUserOrg}>
      <McMemory />
    </McPageShell>
  );
}

const CHANGE_LABELS: Record<McMemoryChange["op"], string> = {
  add: "Added",
  update: "Updated",
  remove: "Removed",
};

/** What a run added to, changed in or removed from memory. Hidden when it made no changes. */
export function RunMemoryChanges({
  changes,
  active,
}: {
  changes: McMemoryChange[];
  active: boolean;
}) {
  if (!changes.length) return null;
  return (
    <Card
      variant="elevated"
      title="Memory changes"
      subtitle={
        active
          ? "Applied when the run finishes"
          : "Applied to memory when the run finished"
      }
    >
      <ul className="flex flex-col gap-3">
        {changes.map((c) => (
          <li key={c.seq} className="flex items-start gap-3">
            <Badge
              size="small"
              variant={c.op === "remove" ? "default" : "info"}
            >
              {CHANGE_LABELS[c.op]}
            </Badge>
            <div className="min-w-0">
              <ClampedText
                text={c.text ?? `Entry ${c.entryId ?? ""}`.trim()}
                lines={3}
                className="text-sm text-gray-800"
              />
              {c.why && (
                <ClampedText
                  text={c.why}
                  lines={2}
                  className="text-[13px] text-gray-500"
                />
              )}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
